import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import { arrayHasValues } from 'helpers/utils'
import { formatPhoneNumber } from 'helpers/phone-number'
import Text from 'components/common/Text/Text'
import Icon from 'components/common/Icon/Icon'
import PhoneLink from 'components/common/PhoneLink'
import Media from 'components/common/Media'
import * as UI from './Common.styles'
import {
  EMPTY_STORE_MESSAGE,
  EMPTY_CART_MESSAGE,
  DROP_OFF_TIMES,
  CALL_STORE_MESSAGE_PREFIX,
  CALL_STORE_MESSAGE_SUFFIX,
  DISABLED_STATE_CALL_STORE_PREFIX,
  DISABLED_STATE_CALL_STORE_SUFFIX,
  TIME_SLOTS_ID,
  SELECTED_DAY_MESSAGE
} from './constants'
import Datepicker from '../Datepicker/index'
import ScheduleBlock from '../ScheduleBlock'
import NextDayTimeTable from './NextDayTimeTable'
import { DROP_OFF_TIME_FIELD_NAME } from 'constant'
import { getFormattedDate } from './../../helpers'

const DateTimeSelector = ({
  handleDateSelect,
  handleDateTimeSelect,
  dates,
  input: {
    value: {
      time = {}
    } = {}
  } = {},
  currentDate,
  timeBlocks = [],
  expandable,
  expanded,
  toggleHandlers: {
    onClick
  },
  hasStore,
  noStoreMessage = EMPTY_STORE_MESSAGE,
  firstDateSelected,
  setFirstDateSelected,
  storePhoneNumber,
  isCartEmpty,
  splitTimeBlocks = [],
  showNextDay,
  isCurrentDateSelected,
  isMobileState
}) =>
  <Fragment>
    {
      hasStore ?
        <UI.MyColumns adaptive width="480px auto" special={ !arrayHasValues(timeBlocks) }>
          <Datepicker
            currentDate={ currentDate }
            dates={ dates }
            firstDateSelected={ firstDateSelected }
            setFirstDateSelected={ setFirstDateSelected }
            onDateSelect={ handleDateSelect } />
          {
            arrayHasValues(timeBlocks) ?
              <UI.Schedule id={ TIME_SLOTS_ID }>
                {
                  dates &&
                    <Fragment>
                      {
                        arrayHasValues(timeBlocks) &&
                        <Fragment>
                          <Text
                            data-at-appointment-dropoff-title
                            follow_link
                            block
                            centered
                            narrow
                            bolder>
                            { DROP_OFF_TIMES }
                          </Text>
                          {
                            showNextDay &&
                            <UI.Description block centered margin={ '15px 0 5px' }>
                              {`${SELECTED_DAY_MESSAGE}`}
                              <Text bold>
                                {`${getFormattedDate(currentDate)}`}
                              </Text>
                            </UI.Description>
                          }
                        </Fragment>
                      }
                      <UI.Times>
                        <UI.CustomColumns width="50% 50%">
                          {
                            splitTimeBlocks.map((timeBlocksSplitted, index) =>
                              <fieldset key={ index }>
                                <legend data-sr-only>{ DROP_OFF_TIMES }</legend>
                                {
                                  timeBlocksSplitted.map(({ displayName, value }) =>
                                    <ScheduleBlock
                                      key={ value }
                                      displayName={ displayName }
                                      name={ DROP_OFF_TIME_FIELD_NAME }
                                      value={ value }
                                      selected={ isCurrentDateSelected && time && time.value === value }
                                      date={ currentDate }
                                      onSelect={ handleDateTimeSelect } />
                                  )
                                }
                              </fieldset>
                            )
                          }
                        </UI.CustomColumns>
                      </UI.Times>
                      {
                        expandable &&
                        <UI.Drop type="button" link { ...getOnClickAndOnKeyPressDownEvents(onClick) } >
                          <Icon
                            alt=""
                            data-at-schedule-less-icon
                            asset="arrow-gray-up"
                            alternative="arrow-red-up"
                            condition={ expanded }
                            margin="0 14px 0 0" />
                          <Text
                            data-at-schedule-more-less-link
                            follow_link
                            size="18"
                            weight="500"> {expanded ? 'Show Less' : 'See More'}</Text>
                          <Icon
                            data-at-schedule-more-icon
                            alt=""
                            asset="arrow-red-down"
                            alternative="arrow-gray-down"
                            condition={ expanded }
                            margin="0 0 0 14px" />
                        </UI.Drop>
                      }
                    </Fragment>
                }
                <UI.Description
                  data-at-appoinment-nostore-message
                  block
                  centered>
                  <Media.Desktop>
                    { `${CALL_STORE_MESSAGE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${CALL_STORE_MESSAGE_SUFFIX}` }
                  </Media.Desktop>
                  <Media.Mobile>
                    { CALL_STORE_MESSAGE_PREFIX }
                    <PhoneLink number={ storePhoneNumber } />
                    { CALL_STORE_MESSAGE_SUFFIX }
                  </Media.Mobile>
                </UI.Description>
                {
                  showNextDay &&
                  <NextDayTimeTable
                    dates={ dates }
                    currentDate={ currentDate }
                    handleDateTimeSelect={ handleDateTimeSelect }
                    time={ time }
                    isMobileState={ isMobileState }
                    isCurrentDateSelected={ isCurrentDateSelected }
                    onNextDaySelect={ handleDateSelect } />
                }
              </UI.Schedule> : !isCartEmpty ?
                <UI.Description
                  data-at-appoinment-nostore-message
                  block
                  centered>
                  <Media.Desktop>
                    { `${DISABLED_STATE_CALL_STORE_PREFIX}${formatPhoneNumber(storePhoneNumber)}${DISABLED_STATE_CALL_STORE_SUFFIX}` }
                  </Media.Desktop>
                  <Media.Mobile>
                    { DISABLED_STATE_CALL_STORE_PREFIX }
                    <PhoneLink number={ storePhoneNumber } />
                    { DISABLED_STATE_CALL_STORE_SUFFIX }
                  </Media.Mobile>
                </UI.Description> :
                <UI.Description block centered>
                  { EMPTY_CART_MESSAGE }
                </UI.Description>
          }
        </UI.MyColumns> :
        <UI.Message data-at-appoinment-nostore-message>{ noStoreMessage }</UI.Message>
    }
  </Fragment>

DateTimeSelector.propTypes = {
  currentDate: PropTypes.string,
  dates: PropTypes.shape({}),
  expandable: PropTypes.bool,
  expanded: PropTypes.bool,
  firstDateSelected: PropTypes.bool,
  handleDateSelect: PropTypes.func,
  handleDateTimeSelect: PropTypes.func,
  handleTimeSelect: PropTypes.func,
  hasStore: PropTypes.bool,
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.shape({
        time: PropTypes.shape({
          displayName: PropTypes.string,
          value: PropTypes.string
        })
      }),
      PropTypes.string
    ])
  }),
  isCartEmpty: PropTypes.bool,
  isCurrentDateSelected: PropTypes.bool,
  isMobileState: PropTypes.bool,
  noStoreMessage: PropTypes.string,
  setFirstDateSelected: PropTypes.func,
  showNextDay: PropTypes.bool,
  splitTimeBlocks: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape())),
  storePhoneNumber: PropTypes.string,
  timeBlocks: PropTypes.arrayOf(PropTypes.shape()),
  toggleHandlers: PropTypes.shape({
    onClick: PropTypes.func
  })
}

export default DateTimeSelector
